/**
 * /app/$tenantSlug/shifts/swaps — Shift Swaps.
 *
 * Two-stage swap workflow surface: create a swap request, the named
 * counterparty accepts (-> pending_manager), then the requester's manager / HR
 * approves (-> swaps both roster rows). All authorization is server-enforced;
 * this page shows every swap the caller can see and the action available to
 * them per status.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import {
  useSwaps,
  useCreateSwap,
  useAcceptSwap,
  useApproveSwap,
  useRejectSwap,
  useCancelSwap,
} from "@/hooks/use-shifts";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/shifts/swaps",
)({
  component: SwapsRoute,
});

function SwapsRoute() {
  const { user } = useAuth();
  const myId = user?.user_id ?? 0;
  const swaps = useSwaps();
  const create = useCreateSwap();
  const accept = useAcceptSwap();
  const approve = useApproveSwap();
  const reject = useRejectSwap();
  const cancel = useCancelSwap();

  const [reqDate, setReqDate] = useState("");
  const [cpId, setCpId] = useState("");
  const [cpDate, setCpDate] = useState("");
  const [note, setNote] = useState("");

  async function submit() {
    const counterpartyId = Number(cpId);
    if (!reqDate || !cpDate || !counterpartyId) {
      toast.error("Fill all swap fields");
      return;
    }
    try {
      await create.mutateAsync({
        requester_date: reqDate,
        counterparty_id: counterpartyId,
        counterparty_date: cpDate,
        note: note.trim() || null,
      });
      toast.success("Swap requested");
      setReqDate("");
      setCpId("");
      setCpDate("");
      setNote("");
    } catch {
      toast.error("Failed to create swap");
    }
  }

  return (
    <TimeAndLeaveShell
      title="Shift Swaps"
      description="Request a swap, accept one, or approve as a manager."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Request a swap</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="rd">My date</Label>
              <Input id="rd" type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cid">Counterparty employee id</Label>
              <Input id="cid" type="number" value={cpId} onChange={(e) => setCpId(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cd">Their date</Label>
              <Input id="cd" type="date" value={cpDate} onChange={(e) => setCpDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="note">Note (optional)</Label>
            <Textarea id="note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </div>
          <Button onClick={submit} disabled={create.isPending}>
            Request swap
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Swaps</CardTitle>
        </CardHeader>
        <CardContent>
          {swaps.data && swaps.data.swaps.length > 0 ? (
            <ul className="divide-y text-sm">
              {swaps.data.swaps.map((s) => {
                const iAmCounterparty = s.counterparty_id === myId;
                const iAmRequester = s.requester_id === myId;
                return (
                  <li key={s.id} className="space-y-1 py-3">
                    <div className="flex items-center justify-between">
                      <span>
                        #{s.requester_id} ({s.requester_date}) ↔ #{s.counterparty_id} (
                        {s.counterparty_date})
                      </span>
                      <Badge variant="outline">{s.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {s.status === "pending_counterparty" && iAmCounterparty && (
                        <Button size="sm" onClick={() => accept.mutate(s.id)}>
                          Accept
                        </Button>
                      )}
                      {s.status === "pending_manager" && (
                        <Button size="sm" onClick={() => approve.mutate({ id: s.id })}>
                          Approve
                        </Button>
                      )}
                      {(s.status === "pending_counterparty" ||
                        s.status === "pending_manager") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject.mutate({ id: s.id })}
                        >
                          Reject
                        </Button>
                      )}
                      {iAmRequester &&
                        (s.status === "pending_counterparty" ||
                          s.status === "pending_manager") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancel.mutate(s.id)}
                          >
                            Cancel
                          </Button>
                        )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">No swaps yet.</p>
          )}
        </CardContent>
      </Card>
      </div>
    </TimeAndLeaveShell>
  );
}
